import { apiUrls, deviceCreateStatuses, deviceCreateTypes } from 'consts'
import { useEffect } from 'react'
import getAll from 'utils/getAll'
import loadDevice from './../utils/loadDevice'
import Store from 'redux/Store'
import { setSaveDeviceAndPassData } from 'redux/actions'

interface useInitProps {
  getValues: Function
  id: string | undefined
  isDeviceReplacement: boolean
  reset: Function
  setCarriers: Function
  setCenters: Function
  setAllCenters: Function
  setFailures: Function
  setManufacturers: Function
  setCurrentManufacturer: Function
  setModels: Function
  setAllModels: Function
  setSites: Function
  setCurrentSite: Function
  setTypes: Function
  setServiceStatuses: Function
  setDataLoaded: Function
  setShowOverlay: Function
  setEnableRmaNumber: Function
  setEnableFailureReason: Function
  setCurrentFailureReason: Function
  currentCustomer: {} | null
  setValue: Function
  setIsReplacementServiceStatus: Function
}

const useInit = (props: useInitProps) => {
  useEffect(() => {
    let defaultCarrier: any = null
    const carriersRequest = getAll(apiUrls.carriers).then((data) => {
      props.setCarriers(data)
      defaultCarrier = data[0] ?? null
    })

    let allCenters: Array<any> = []
    const centersRequest = getAll(apiUrls.centers).then((data) => {
      props.setAllCenters(data)
      allCenters = data
    })

    const failuiresRequest = getAll(apiUrls.failures).then((data) => props.setFailures(data))

    let defaultManufacturer: any = null
    const manufacturersRequest = getAll(apiUrls.manufacturers).then((data) => {
      props.setManufacturers(data)
      defaultManufacturer = data[0] ?? null
    })

    let allModels: Array<any> = []
    const modelsRequest = getAll(apiUrls.models).then((data) => {
      props.setAllModels(data)
      allModels = data
    })

    let defaultType: any = null
    const typesRequest = getAll(apiUrls.types).then((data) => {
      props.setTypes(data)
      defaultType = data[0] ?? null
    })

    let defaultSite: any = null
    const sitesRequest = getAll(apiUrls.sites).then((data) => {
      props.setSites(data)
      defaultSite = data[0] ?? null
    })

    let defaultServiceStatus: any = null
    const serviceStatusesRequest = getAll(apiUrls.serviceStatuses).then((data) => {
      props.setServiceStatuses(data)
      defaultServiceStatus = data[0] ?? null
    })

    // fetch the device if edit
    if (props.id !== undefined) {
      loadDevice(props.id).then((data) => {
        if (props.isDeviceReplacement) {
          data['location'] = ''
          data['last_used_date'] = ''
          data['last_used_by'] = ''
          data['mac_address'] = ''
          data['serial_number'] = ''
          data['origin_rma'] = data.rma
          data['rma'] = ''
          data['serviceStatus'] = null
          data['service_status_id'] = null
          data['failure'] = null
          data['failure_id'] = null
        }

        Promise.all([
          carriersRequest,
          centersRequest,
          failuiresRequest,
          modelsRequest,
          manufacturersRequest,
          sitesRequest,
          typesRequest,
          serviceStatusesRequest,
        ]).then(() => {
          props.reset(data)

          props.setValue(
            'create_status_label',
            deviceCreateStatuses.find((s) => s.id === data.create_status)?.name ?? ''
          )
          props.setValue(
            'create_type_label',
            deviceCreateTypes.find((t) => t.id === data.create_type)?.name ?? ''
          )

          if (props.isDeviceReplacement) {
            props.setValue('serviceStatus', defaultServiceStatus)
            props.setEnableRmaNumber(defaultServiceStatus?.allow_rma_edit === 1)
            props.setIsReplacementServiceStatus(defaultServiceStatus?.init_replacement === 1)
          } else {
            props.setEnableRmaNumber(data?.serviceStatus?.allow_rma_edit === 1)
            props.setIsReplacementServiceStatus(data?.serviceStatus?.init_replacement === 1)
          }

          props.setCurrentManufacturer(data.manufacturer_id)
          props.setCurrentSite(data.site_id)

          props.setEnableFailureReason(data.rma?.length > 0)
          props.setCurrentFailureReason(data.failure?.id)

          const filteredCenters = allCenters.filter((obj) => {
            return obj.site_id === data.site_id
          })
          props.setCenters(filteredCenters)

          const filteredModels = allModels.filter((obj) => {
            return obj.manufacturer_id === data.manufacturer_id
          })
          props.setModels(filteredModels)
          //set correct model and center after data filtering
          props.setValue('model', data?.model)
          props.setValue('center', data?.center)

          //fix null string validation
          if (data?.serial_number === null) {
            props.setValue('serial_number', '')
          }

          //fix null string validation
          if (data?.label === null) {
            props.setValue('label', '')
          }

          props.setDataLoaded(true)
          props.setShowOverlay(false)
        })
      })
    } else {
      Promise.all([
        carriersRequest,
        centersRequest,
        failuiresRequest,
        modelsRequest,
        manufacturersRequest,
        sitesRequest,
        typesRequest,
        serviceStatusesRequest,
      ]).then(() => {
        // check if there's saved data from "Save & New"
        const savedData = Store.getState().saveDeviceAndPassData

        if (savedData) {
          const filteredModels = allModels.filter((obj) => {
            return obj.manufacturer_id === savedData.manufacturer?.id
          })
          props.setModels(filteredModels)

          const filteredCenters = allCenters.filter((obj) => {
            return obj.site_id === savedData.site?.id
          })
          props.setCenters(filteredCenters)

          props.setValue('type', savedData.type)
          props.setValue('manufacturer', savedData.manufacturer)
          props.setValue('carrier', savedData.carrier)
          props.setValue('site', savedData.site)
          props.setValue('production_date', savedData.production_date)
          props.setValue('warranty_expiration_date', savedData.warranty_expiration_date)
          props.setValue('serviceStatus', savedData.serviceStatus)
          props.setEnableRmaNumber(savedData.serviceStatus?.allow_rma_edit === 1)
          props.setIsReplacementServiceStatus(savedData.serviceStatus?.init_replacement === 1)
          props.setCurrentManufacturer(savedData.manufacturer?.id)
          props.setCurrentSite(savedData.site?.id)
          props.setValue('model', savedData.model)
          props.setValue('center', savedData.center)

          // Clear the saved data from store
          Store.dispatch(setSaveDeviceAndPassData(null))
        } else {
          // Use default values
          const filteredModels = allModels.filter((obj) => {
            return obj.manufacturer_id === defaultManufacturer?.id
          })
          const defaultModel = filteredModels[0] ?? null
          props.setModels(filteredModels)

          const filteredCenters = allCenters.filter((obj) => {
            return obj.site_id === defaultSite?.id
          })
          const defaultCenter = filteredCenters[0] ?? null
          props.setCenters(filteredCenters)

          props.setValue('type', defaultType)
          props.setValue('manufacturer', defaultManufacturer)
          props.setValue('model', defaultModel)
          props.setValue('center', defaultCenter)
          props.setValue('carrier', defaultCarrier)
          props.setValue('site', defaultSite)
          props.setValue('serviceStatus', defaultServiceStatus)
          props.setEnableRmaNumber(defaultServiceStatus?.allow_rma_edit === 1)
          props.setIsReplacementServiceStatus(defaultServiceStatus?.init_replacement === 1)
          props.setCurrentManufacturer(defaultManufacturer?.id)
          props.setCurrentSite(defaultSite?.id)

          //reset center when customer has been changed
          props.setValue('center', null)
        }

        props.setDataLoaded(true)
        props.setShowOverlay(false)
      })
    }
  }, [props.currentCustomer])
}

export default useInit
